import { useEffect, useRef } from 'react'
import { toast } from 'sonner'

import { createLogger } from '@/lib/logger'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { isTauri } from '@/lib/utils/tauri'
import {
  deleteLocalRecording,
  getRecordingState,
  listenForCaptionStatus,
  listenForRecordingAutoStopped,
  listenForRecordingCaption,
  listenForRecordingError,
  listenForRecordingLevel,
  listenForRecordingOrphans,
  listenForRecordingSystemAudio,
  openRecordingPrivacySettings,
  listenForRecordingState,
  listenForRecordingUploadComplete,
  listenForRecordingUploadProgress,
  listLocalRecordings,
  uploadRecording,
  type RecordingInfo,
} from '@/lib/utils/tauri-recording'
import { meetingRecordingsApi } from '@/lib/api/meeting-recordings.api'
import {
  clearStoredScratchpad,
  getStoredScratchpad,
  useRecordingStore,
} from '@/store/recording.store'

const logger = createLogger('recorder-controller')

/**
 * Headless controller for the desktop meeting recorder. Mounted once in
 * MainLayout (desktop only). Mirrors Rust recorder state into the zustand
 * store, surfaces errors, handles orphan-recovery and auto-upload on stop.
 */
export function RecorderController() {
  const startedUploadsRef = useRef(new Set<string>())
  const { checkFeatureFlag } = useFeatureFlags()
  const isMeetingNotesEnabled = checkFeatureFlag('MEETING_NOTES')

  useEffect(() => {
    if (!isTauri() || !isMeetingNotesEnabled) return

    let disposed = false
    const unlisteners: (() => void)[] = []

    // Refresh the persistent "pending uploads" list from disk so any
    // recording that hasn't reached the cloud (failed/interrupted upload, or
    // never started — survives crashes AND app updates via app_data_dir) is
    // discoverable and retryable, not just via a one-time startup toast.
    const refreshPending = async () => {
      try {
        const local = await listLocalRecordings()
        if (!disposed) useRecordingStore.getState().setPendingUploads(local)
      } catch (error) {
        logger.error('Failed to list local recordings', error)
      }
    }

    const handleUpload = async (recording: RecordingInfo | string) => {
      const id = typeof recording === 'string' ? recording : recording.id
      // Skip recordings the user discarded — delete the local file instead.
      if (useRecordingStore.getState().isDiscarded(id)) {
        clearStoredScratchpad(id)
        void deleteLocalRecording(id)
          .catch(() => {})
          .finally(() => useRecordingStore.getState().clearDiscarded(id))
        return
      }
      if (startedUploadsRef.current.has(id)) return
      startedUploadsRef.current.add(id)
      try {
        await uploadRecording(id)
      } catch (error) {
        startedUploadsRef.current.delete(id)
        logger.error('Recording upload failed', error)
        // Keep it visible/retryable in the persistent list, not just here.
        void refreshPending()
        toast.error('Recording upload failed', {
          description: String(
            error instanceof Error ? error.message : error,
          ).slice(0, 200),
          action: {
            label: 'Retry',
            onClick: () => void handleUpload(id),
          },
          duration: 30_000,
        })
      }
    }

    const setup = async () => {
      const store = useRecordingStore.getState()

      // Hydrate from Rust (handles window reloads mid-recording)
      const state = await getRecordingState()
      if (state && !disposed) {
        store.applyStatePayload({
          state: state.state,
          recordingId: state.recordingId,
          startedAt: state.startedAt,
          durationMs: state.durationMs,
        })
      }

      // Surface any locally-stored recordings that haven't reached the cloud
      // (kept across crashes and app updates) so they're always retryable.
      void refreshPending()

      unlisteners.push(
        await listenForRecordingState(payload => {
          useRecordingStore.getState().applyStatePayload(payload)
        }),
      )
      unlisteners.push(
        await listenForRecordingLevel(payload => {
          useRecordingStore.getState().setLevels(payload)
        }),
      )
      unlisteners.push(
        await listenForRecordingError(payload => {
          useRecordingStore.getState().setError(payload)
          logger.error('Recording error', payload)
          toast.error('Recording error', { description: payload.message })
        }),
      )
      unlisteners.push(
        await listenForRecordingUploadProgress(payload => {
          useRecordingStore
            .getState()
            .setUploadProgress(payload.recordingId, {
              uploadedBytes: payload.uploadedBytes,
              totalBytes: payload.totalBytes,
            })
        }),
      )
      unlisteners.push(
        await listenForRecordingUploadComplete(payload => {
          startedUploadsRef.current.delete(payload.recordingId)
          useRecordingStore.getState().removePendingUpload(payload.recordingId)
          // Ship the in-meeting scratchpad so the summarizer merges it. This
          // lands during transcription (minutes before summarize reads the
          // recording), so it always makes the summary.
          const scratchpad = getStoredScratchpad(payload.recordingId)
          if (scratchpad.trim()) {
            void meetingRecordingsApi
              .updateUserNotes(payload.apiRecordingId, scratchpad.slice(0, 50000))
              .then(() => clearStoredScratchpad(payload.recordingId))
              .catch(error =>
                logger.error('Failed to attach scratchpad notes', error),
              )
          }
          toast.success('Recording uploaded', {
            description: 'AI notes are being generated — check Recordings.',
          })
        }),
      )
      unlisteners.push(
        await listenForRecordingSystemAudio(payload => {
          useRecordingStore.getState().setSystemAudioCaptured(payload.captured)
          if (!payload.captured && payload.reason === 'permission_denied') {
            toast.warning('Recording your microphone only', {
              description:
                'System audio is off, so other participants won’t be captured. Enable it to record everyone.',
              action: {
                label: 'Open Settings',
                onClick: () =>
                  void openRecordingPrivacySettings('systemAudio'),
              },
              duration: 12_000,
            })
          }
        }),
      )
      unlisteners.push(
        await listenForRecordingCaption(payload => {
          useRecordingStore.getState().appendCaption(payload)
        }),
      )
      unlisteners.push(
        await listenForCaptionStatus(payload => {
          useRecordingStore
            .getState()
            .setCaptionsActive(
              payload.active
                ? true
                : payload.reason === 'starting'
                  ? 'starting'
                  : false,
            )
        }),
      )
      unlisteners.push(
        await listenForRecordingAutoStopped(() => {
          toast.info('Recording reached the 2-hour limit', {
            description:
              'Stopping and generating notes from what was captured.',
          })
        }),
      )
      unlisteners.push(
        await listenForRecordingOrphans(orphans => {
          if (!orphans.length) return
          useRecordingStore.getState().setPendingUploads(orphans)
          toast.info(
            orphans.length === 1
              ? 'A recording from a previous session was not uploaded'
              : `${orphans.length} recordings from previous sessions were not uploaded`,
            {
              action: {
                label: 'Upload',
                onClick: () => {
                  for (const orphan of orphans) void handleUpload(orphan)
                },
              },
              cancel: {
                label: 'Discard',
                onClick: () => {
                  for (const orphan of orphans) {
                    void deleteLocalRecording(orphan.id).then(() =>
                      useRecordingStore
                        .getState()
                        .removePendingUpload(orphan.id),
                    )
                  }
                },
              },
              duration: 60_000,
            },
          )
        }),
      )

      // When a recording finishes ("recorded" state on disk), upload it.
      // The state event fires on transition to idle; check local files then.
      unlisteners.push(
        await listenForRecordingState(payload => {
          if (payload.state !== 'idle') return
          void listLocalRecordings().then(local => {
            for (const recording of local) {
              if (recording.state === 'recorded') void handleUpload(recording)
            }
          })
        }),
      )
    }

    void setup().catch(error =>
      logger.error('Recorder controller setup failed', error),
    )

    return () => {
      disposed = true
      unlisteners.forEach(unlisten => unlisten())
    }
  }, [isMeetingNotesEnabled])

  return null
}
